import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useNarrowViewport } from '@/layouts/shellChrome';
import { PhoneScreen } from '@/components/kit';
import { NurseMenu } from './NurseMenu';
import { NoteField, type NoteFieldHandle } from './NoteField';
import { useTaskActions, type NurseTask } from '../api';
import { CANT_REASONS, cantDoes, drName, execTitle } from '../format';

/**
 * CAN'T — THE REASON, AND WHERE IT GOES (g_cant).
 *
 * Countable reasons, never free text only, plus the note. The note is optional
 * and REQUIRED for "Other" — enforced here and again by the backend. The kit's
 * ReasonPicker appends its own "Other" row with different copy, so the nurse
 * canvas's own six rows are drawn here rather than bent through it.
 *
 * Covers the whole screen, rail and pane included, the way the canvas scrims
 * it — so it is portalled to the body instead of living inside the content
 * column the page renders into.
 */
/** Opened from My day (`ison.cantd` / `ison.cantj`): the three destinations. */
export function DayCantSub() {
  return (
    <>
      Three destinations, always the same — the doctor is notified · an attributed line on the task · the task
      closes, never silently
    </>
  );
}

export function CantModal({
  task,
  open,
  onClose,
  onDone,
  sub = 'Countable reasons — never free text only — plus the note. The list is pack-extendable per clinic.',
}: {
  task: NurseTask;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  /** The line under the title — the caller's: Execute and My day say different things. */
  sub?: ReactNode;
}) {
  const { cant } = useTaskActions();
  const narrow = useNarrowViewport();
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const noteRef = useRef<NoteFieldHandle>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const needsNote = picked === 'other';
  const ready = picked !== null && (!needsNote || note.trim().length > 0);
  const to = drName(task.requestedBy);

  const send = () => {
    if (!picked) {
      toast.error('Pick a reason first — it is what makes it countable');
      return;
    }
    if (!ready) {
      toast.error('“Other” needs the reason written in the note');
      return;
    }
    cant.mutate(
      { id: task.id, reason: picked, note: note.trim() },
      {
        onSuccess: () => {
          toast.success(to ? `Sent back to ${to}` : 'Closed with the reason');
          setPicked(null);
          setNote('');
          onDone();
        },
        onError: (e) => toast.error((e as Error).message || 'Could not send it back'),
      },
    );
  };

  /* g_n7: on a phone Can't is a screen, not a dialog — the ☰ · ‹ strip with
     "Injection · Omar K. ▸ Can't", the reasons, the note, the one send. */
  if (narrow) {
    return createPortal(
      <div className="maie" style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'var(--surface-page, var(--bg, #fff))' }}>
        <PhoneScreen
          crumb={execTitle(task)}
          title="Can’t"
          onBack={onClose}
          menu={(close) => <NurseMenu door="day" onClose={close} />}
        >
          <p className="t-sec mut">Why not?</p>
          <div className="mt8" style={{ display: 'grid', gap: 8 }}>
            {CANT_REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                className="card"
                aria-pressed={picked === r.key}
                onClick={() => setPicked(r.key)}
                style={{
                  margin: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderColor: picked === r.key ? 'var(--danger-600, #b42318)' : undefined,
                }}
              >
                <span className={`t-body${picked === r.key ? ' w6 red' : ''}`}>{r.label}</span>
              </button>
            ))}
          </div>
          <div className="card card-ghost mt12" style={{ cursor: 'text' }}
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest('.nurse-note')) noteRef.current?.focus();
            }}>
            <p className="t-ovl mut">
              Note{' '}
              <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional, one line)</span>
            </p>
            <p className="t-sec sec mt4">
              <NoteField ref={noteRef} label="Note" value={note} onChange={setNote} maxLength={300}
                placeholder={needsNote ? 'write the reason — it is the record' : 'one line, in your words'} />
            </p>
          </div>
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn"
              style={{ background: 'var(--warning-600)', color: 'var(--surface-0)', paddingInline: 24 }}
              disabled={cant.isPending}
              onClick={send}
            >
              {to ? `Send back to ${to}` : 'Close with the reason'}
            </button>
          </div>
          <div className="card card-acc mt12">
            <p className="t-sec brand">
              → the doctor’s needs-you chip · an attributed line in the note · the task closes, never silently
            </p>
          </div>
        </PhoneScreen>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="maie desk kit-modal-dock" style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div className="dscrim" onClick={onClose} role="presentation" />
      <div
        className="dmodal dmodal-phone"
        style={{ width: 'min(620px, calc(100vw - 32px))' }}
        role="dialog"
        aria-modal="true"
        aria-label={`Can’t — ${execTitle(task)}`}
      >
        <p className="t-title">Can’t — {execTitle(task)}</p>
        <p className="t-sec mut">{sub}</p>
        {narrow ? (
          <div className="mt12" style={{ display: 'grid', gap: 8 }}>
            {CANT_REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                className="card"
                aria-pressed={picked === r.key}
                onClick={() => setPicked(r.key)}
                style={{
                  margin: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderColor: picked === r.key ? 'var(--danger-600, #b42318)' : undefined,
                }}
              >
                <span className={`t-body${picked === r.key ? ' w6' : ''}`}>{r.label}</span>
              </button>
            ))}
          </div>
        ) : (
        <table className="dtable" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ width: '48%' }}>Reason</th>
              <th style={{ width: '52%' }}>What it does</th>
            </tr>
          </thead>
          <tbody>
            {CANT_REASONS.map((r) => (
              <tr
                key={r.key}
                className={picked === r.key ? 'rowc sel' : 'rowc'}
                onClick={() => setPicked(r.key)}
                tabIndex={0}
                aria-selected={picked === r.key}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPicked(r.key);
                  }
                }}
              >
                <td><span className="t-body">{r.label}</span></td>
                <td><span className="t-sec sec">{cantDoes(r, task)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

        {/* The note is running text, quoted, with the pencil right after it —
            “says he had a reaction to it before” ✎ — as the canvas draws it. */}
        <div className="card card-ghost mt12" style={{ cursor: 'text' }}
          onClick={(e) => {
            if (!(e.target as HTMLElement).closest('.nurse-note')) noteRef.current?.focus();
          }}>
          <p className="t-ovl mut">
            Note{' '}
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
              (optional — required if you pick “Other”)
            </span>
          </p>
          <p className="t-sec sec mt4">
            <NoteField ref={noteRef} label="Note" value={note} onChange={setNote} maxLength={300}
              placeholder={needsNote ? 'write the reason — it is the record' : 'one line, in your words'} />
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn"
            style={{ background: 'var(--warning-600)', color: 'var(--surface-0)' }}
            disabled={cant.isPending}
            onClick={send}
          >
            {to ? `Send back to ${to}` : 'Close with the reason'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
